import React from 'react';
import type { CalendarItem } from '@/types/contentPlanner';

// Badge labels are the SAME five status groups the Content Planner summary cards
// count (backend ContentRepository.get_calendar_items), so a row's badge always
// names the exact tile that counts it. Keep this in sync with that function.
export type StatusVariant = 'In Progress' | 'Ready' | 'Scheduled' | 'Published' | 'Failed';

export interface StatusPillProps {
  status: StatusVariant;
  size?: 'sm' | 'md';
}

interface PillTokens {
  bg: string;
  fg: string;
  dot: string;
  border?: string;
}

// Colours track the matching summary-card tile in SummaryStrip.tsx.
const PILL_TOKENS: Record<StatusVariant, PillTokens> = {
  'In Progress': {
    bg: 'var(--color-teal-light)',
    fg: 'var(--color-teal-dark)',
    dot: 'var(--color-teal-dark)',
  },
  Ready: {
    bg: 'var(--color-coral-light)',
    fg: 'var(--color-coral-dark)',
    dot: 'var(--color-coral)',
  },
  Scheduled: {
    bg: 'var(--color-amber-light)',
    fg: 'var(--color-amber-dark)',
    dot: 'var(--color-amber)',
  },
  // Published maps to the primary accent (matches StatusBadge/StatusTag, where
  // "published" is the primary brand colour) rather than the secondary/neutral
  // used for In Progress — the two components previously disagreed.
  Published: {
    bg: 'var(--color-coral-light)',
    fg: 'var(--color-coral-dark)',
    dot: 'var(--color-coral)',
  },
  Failed: {
    bg: 'color-mix(in srgb, var(--color-destructive) 14%, transparent)',
    fg: 'var(--color-destructive)',
    dot: 'var(--color-destructive)',
  },
};

export const StatusPill: React.FC<StatusPillProps> = ({ status, size = 'sm' }) => {
  const tokens = PILL_TOKENS[status] ?? PILL_TOKENS.Ready;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: size === 'sm' ? '2px 8px 2px 7px' : '4px 10px 4px 8px',
        borderRadius: 12,
        background: tokens.bg,
        color: tokens.fg,
        fontSize: size === 'sm' ? 11 : 12,
        fontWeight: 600,
        border: tokens.border ?? 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 3,
          background: tokens.dot,
        }}
      />
      {status}
    </span>
  );
};

// Mirrors the backend summary-card groups (ContentRepository.get_calendar_items):
// published_condition / scheduled_condition / failed_condition /
// generating_condition, with "Ready" as the remainder. The backend counts those
// groups independently and a row can satisfy several at once (a live post may
// still carry a schedule stamp), so this precedence — Published > Failed >
// Scheduled > In Progress > Ready — collapses them to the single most meaningful
// label. Every returned label names a tile that genuinely counts the row.
export function deriveStatusVariant(item: CalendarItem): StatusVariant {
  const status = item.status;

  // Published — a live post exists (mirrors published_condition).
  if (
    status === 'published' ||
    item.wordpress_status === 'publish' ||
    item.wp_url != null
  ) return 'Published';

  // Failed — surfaced ahead of any stray schedule stamp so failures stay visible.
  if (status === 'failed') return 'Failed';

  // Scheduled — backend-computed calendar placement (scheduled_publish_at/time or
  // publish_status === 'scheduled'), exposed as is_scheduled.
  if (item.is_scheduled || item.publish_status === 'scheduled') return 'Scheduled';

  // In Progress — the whole active-generation set (mirrors generating_condition).
  if (
    status === 'pending' ||
    status === 'in_progress' ||
    status === 'generating' ||
    status === 'analyzing_serp' ||
    status === 'generating_brief' ||
    status === 'outline_pending' ||
    status === 'writing_content' ||
    status === 'validating' ||
    status === 'publishing'
  ) return 'In Progress';

  // Ready — drafted/finished but not scheduled, generating, published or failed.
  return 'Ready';
}

export default StatusPill;
